import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const NotFoundContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 70vh;
  padding: 2rem;
  text-align: center;
`;

const Title = styled(motion.h1)`
  font-size: 10rem;
  margin: 0;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 6rem;
  }
`;

const Subtitle = styled(motion.h2)`
  font-size: 2.5rem;
  margin-top: 0;
  margin-bottom: 2rem;
  color: ${({ theme }) => theme.colors.text};

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const Description = styled(motion.p)`
  font-size: 1.2rem;
  max-width: 600px;
  margin-bottom: 2.5rem;
  color: ${({ theme }) => theme.colors.textLight};
`;

const StyledLink = styled(motion(Link))`
  padding: 0.8rem 2rem;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const NotFound = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "404 - Page Not Found | D-Soft";
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const childVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { type: "spring", stiffness: 120 }
    }
  };

  return (
    <NotFoundContainer as={motion.div} 
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <Title variants={childVariants}>404</Title>
      <Subtitle variants={childVariants}>Page Not Found</Subtitle>
      <Description variants={childVariants}>
        The page you are looking for might have been removed, had its name changed,
        or is temporarily unavailable.
      </Description>
      <StyledLink 
        to="/" 
        variants={childVariants}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        Return to Homepage
      </StyledLink>
    </NotFoundContainer>
  );
};

export default NotFound; 